/**
 * „Dein erster Run": drei Aufgaben zur Wahl, je ein Satz für Claude und was
 * zurückkommt (Entwurf C „Satz und Ergebnis", Canvas „99takes
 * Prompt-Vorschläge“, gewählt von Robert am 14.10.2026, Vorbild die
 * Plugin-Seite von Higgsfield in ChatGPT). Vorher standen hier zwei Schritte
 * mit Lückentext („[Produkt, Zielgruppe, Markt]“, „https://[deine-seite]“),
 * die erst ausgefüllt werden mussten.
 *
 * DIE SÄTZE SIND ECHTE ANWEISUNGEN AN CLAUDE und müssen zu den Werkzeugen
 * passen: „Seite testen“ führt zu test_page (ohne eigene Zielgruppe baut
 * Claude sie zuerst mit start_research auf), „Headlines vergleichen“ zu
 * simulate_copy, „Wettbewerb prüfen“ zu compare_claims. Nichts versprechen,
 * was die Werkzeuge nicht können. Dieselben Sätze stehen im Eintrag im
 * Connectors Directory (tappla/99takes-app#256) und auf der Startseite
 * (Blessing-Digital/99takes-website); wer sie hier ändert, zieht dort nach.
 *
 * Die Reihenfolge richtet sich nach den Antworten nach der Registrierung
 * (onboarding.ts): wer Anzeigen, E-Mails oder Kernaussagen prüfen will,
 * bekommt die Headlines zuerst, wer an der Positionierung arbeitet, den
 * Wettbewerb. Agenturen sprechen vom Kunden, Kampagnen von Wählern.
 */

import type { Gespeichert } from './onboarding';
import { t } from './sprache';

export type AufgabeId = 'seite' | 'headlines' | 'wettbewerb';

export interface Aufgabe {
  id: AufgabeId;
  /** Die Dachzeile über dem Satz. */
  dach: string;
  /** Der Satz, wie er in Claude eingetippt wird. */
  satz: string;
  /** Was zurückkommt, ein Satz. */
  zurueck: string;
}

export interface Vorschlag {
  /** Drei Aufgaben, die passendste zuerst. */
  aufgaben: Aufgabe[];
}

type Leser = 'kunden' | 'waehler';

function aufgabe(id: AufgabeId, agentur: boolean, leser: Leser): Aufgabe {
  if (id === 'seite') {
    return {
      id, dach: t('Seite testen', 'Test a page'),
      satz: agentur
        ? t('Teste die Startseite meines Kunden gegen seine Zielgruppe und schreib eine bessere Fassung.',
          "Test my client's homepage against their audience and write a better version.")
        : t('Teste meine Startseite gegen meine Zielgruppe und schreib eine bessere Fassung.',
          'Test my homepage against my audience and write a better version.'),
      zurueck: t('Zurück kommt die neue Fassung zum Freigeben, dann das Urteil.',
        'You get the new version to approve, then the verdict.'),
    };
  }
  if (id === 'headlines') {
    const bei = leser === 'waehler'
      ? (agentur ? 'bei den Wählerinnen und Wählern meines Kunden' : 'bei meinen Wählerinnen und Wählern')
      : (agentur ? 'bei den Kunden meines Kunden' : 'bei meinen Kunden');
    const among = leser === 'waehler'
      ? (agentur ? "my client's voters" : 'my voters')
      : (agentur ? "my client's customers" : 'my customers');
    return {
      id, dach: t('Headlines vergleichen', 'Compare headlines'),
      satz: t(`Welche dieser zwei Headlines holt mehr Klicks ${bei}?`,
        `Which of these two headlines gets more clicks from ${among}?`),
      zurueck: t('Zurück kommt, welche vorn liegt, und je Persona der Einwand, an dem die andere hängenbleibt.',
        'You get which one is ahead, and for each persona the objection the other one gets stuck on.'),
    };
  }
  return {
    id, dach: t('Wettbewerb prüfen', 'Check the competition'),
    satz: agentur
      ? t('Welche Aussagen auf der Seite meines Kunden machen seine Wettbewerber genauso?',
        "Which claims on my client's page do their competitors make too?")
      : t('Welche Aussagen auf meiner Seite machen meine Wettbewerber genauso?',
        'Which claims on my page do my competitors make too?'),
    zurueck: t('Zurück kommt je Kernaussage: besetzt, umstritten oder frei, mit wörtlichem Zitat und Quelle.',
      'For each key claim you get: taken, contested or free, with a verbatim quote and source.'),
  };
}

/** Welche Aufgabe vorn steht, je Antwort auf „Was willst du vor allem prüfen?". Reihenfolge = Vorrang. */
const ZUERST: Array<[string, AufgabeId]> = [
  ['landingpages', 'seite'],
  ['angebote', 'seite'],
  ['anzeigen', 'headlines'],
  ['emails', 'headlines'],
  ['positionierung', 'wettbewerb'],
];
const GRUND: AufgabeId[] = ['seite', 'headlines', 'wettbewerb'];

export function ersterLaufVorschlag(antworten?: Gespeichert | null): Vorschlag {
  const pruefen = antworten && Array.isArray(antworten.pruefen) ? antworten.pruefen : [];
  const agentur = antworten?.rolle === 'agentur';
  const leser: Leser = antworten?.zielgruppe === 'politik' ? 'waehler' : 'kunden';
  const vorn = ZUERST.find(([k]) => pruefen.includes(k))?.[1] ?? 'seite';
  const folge = [vorn, ...GRUND.filter(id => id !== vorn)];
  return { aufgaben: folge.map(id => aufgabe(id, agentur, leser)) };
}

/** Wohin „Claude öffnen" führt. Ohne Satz im Link: mit `?q=` zeigt claude.ai einen Warnhinweis vor dem Prompt (geprüft 14.10.2026). */
export const CLAUDE_NEU = 'https://claude.ai/new';
